
/**
 * Module dependencies.
 */

import { ForwardedRef, MouseEvent, ReactNode, forwardRef } from 'react';
import { RouterLink } from './links/router-link';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * Export `CardProps` type.
 */

export type CardProps = {
  'aria-rowindex'?: number;
  as?: string | undefined;
  children?: ReactNode;
  className?: string;
  href?: string;
  isClickable?: boolean;
  isSelected?: boolean;
  linkTo?: any;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  role?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isClickable?: boolean; isSelected?: boolean }>`
  background-color: ${color('white')};
  border: 1px solid ${color('gray200')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  padding: 12px;
  position: relative;
  text-decoration: none;
  transition: ${theme('animations.fastTransition')};
  transition-property: background-color, border-color;

  ${ifProp((props: { onClick?: CardProps['onClick']; to?: string }) => props.onClick || props.to, css`
    appearance: none;
    color: inherit;
    cursor: pointer;
    text-decoration: inherit;

    &:active {
      background-color: ${color('blue100')};
      border-color: ${color('blue100')};
    }

    &:focus,
    &:hover {
      background-color: ${color('gray200')};
      outline: none;
    }
  `)}

  ${ifProp('isSelected', css`
    background-color: ${color('blue100')};
  `)}

  ${ifProp('isClickable', css`
    &:focus,
    &:focus-within,
    &:hover {
      background-color: ${color('gray200')};
    }
  `)}

  ${media.min('ms')`
    padding: 16px;
  `}
`;

/**
 * `Card` component.
 */

function Card(props: CardProps, ref: ForwardedRef<HTMLDivElement>): JSX.Element {
  const { as, children, linkTo: to, onClick, ...rest } = props;

  return (
    <Wrapper
      {...rest}
      as={to && RouterLink || onClick && 'button' || as}
      onClick={onClick}
      ref={ref}
      tabIndex={onClick ? 0 : undefined}
      to={to}
    >
      {children}
    </Wrapper>
  );
}

/**
 * Forwarded `Card`.
 */

const ForwardedCard = forwardRef<HTMLDivElement, CardProps>(Card);

/**
 * Export `Card` component.
 */

export { ForwardedCard as Card };
